import React, { useEffect, useMemo, useState } from 'react';
import type { Survey } from './survey.types';
import type { ErrorsMap } from './SurveyPage.types';
import { RadcliffeHeader } from '../layout/RadcliffeHeader';
import { useLanguageStore } from '../../stores/languageStore';
import LanguageHelper from '../../utils/languageHelper';
import { generateSubmissionJSON } from '../../utils/submissionGenerator';
import { appConfig } from '../../config/app-config';
import { SurveyHeader } from './components/SurveyHeader';
import { SurveyProgress } from './components/SurveyProgress';
import { SurveyNavigation } from './components/SurveyNavigation';
import { QuestionRenderer } from './components/QuestionRenderer';
import { SurveySuccessPage } from './components/SurveySuccessPage';
import { useSurveyState } from './hooks/useSurveyState';
import { useSurveyValidation } from './hooks/useSurveyValidation';
import { extractUTMParameters, getMarketoCookie } from '../../utils/trackingUtils';
import { logError } from '../../utils/rollbar';
import '../styles/survey.css';

/**
 * Props for the SurveyPage component
 */
interface SurveyPageProps {
  /** Survey data containing questions, configuration, and metadata */
  surveyData: Survey;
  /** Survey ID from the URL */
  surveyId: string;
  /** Whether the survey is in preview mode (doesn't actually submit) */
  isPreviewMode?: boolean;
}

/**
 * SurveyPage Component
 *
 * Main orchestrator component for the survey experience. This component:
 * - Manages survey state through custom hooks
 * - Coordinates navigation between questions
 * - Handles form validation and submission
 * - Renders sub-components in the correct layout
 *
 * The component delegates specific responsibilities to specialized components:
 * - SurveyHeader: Title, description, and language selector
 * - SurveyProgress: Progress bar and counter (one-at-a-time mode)
 * - QuestionRenderer: Renders the appropriate question component
 * - SurveyNavigation: Navigation buttons (Previous, Next, Submit)
 *
 * State management is handled through custom hooks:
 * - useSurveyState: Manages responses, current question, and errors
 * - useSurveyValidation: Provides validation logic
 *
 * @param props - Component props
 * @returns The complete survey page experience
 *
 * @example
 * <SurveyPage surveyData={surveyData} />
 */
export const SurveyPage: React.FC<SurveyPageProps> = ({ surveyData, surveyId, isPreviewMode = false }) => {
  const languageCode = useLanguageStore((state) => state.code);
  const setLanguage = useLanguageStore((state) => state.setLanguage);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submissionError, setSubmissionError] = useState<string | null>(null);
  const [currentPageIndex, setCurrentPageIndex] = useState(0);
  const [isMobile, setIsMobile] = useState(false);
  const [alreadyCompleted, setAlreadyCompleted] = useState(false);

  // Extract tracking data from URL and cookies on mount
  const [eToken] = useState<string | null>(() => {
    const searchParams = new URLSearchParams(window.location.search);
    return searchParams.get('e');
  });

  const [utmParameters] = useState(() => {
    const searchParams = new URLSearchParams(window.location.search);
    return extractUTMParameters(searchParams);
  });

  const [marketoCookie] = useState(() => {
    return getMarketoCookie();
  });

  // Check for compact mode query parameter
  const [isCompactMode] = useState(() => {
    const searchParams = new URLSearchParams(window.location.search);
    return searchParams.get('compact') === 'true';
  });

  // Check for header override query parameter
  const [headerOverride] = useState<'true' | 'false' | null>(() => {
    const searchParams = new URLSearchParams(window.location.search);
    const headerParam = searchParams.get('header');
    if (headerParam === 'true') return 'true';
    if (headerParam === 'false') return 'false';
    return null;
  });

  // Check for title override query parameter
  const [showTitle] = useState<boolean>(() => {
    const searchParams = new URLSearchParams(window.location.search);
    const titleParam = searchParams.get('title');
    if (titleParam === 'false') return false;
    return true; // Default to showing title
  });

  // Derive header mode from query parameter override, then survey config, with backwards-compatible fallback
  const headerMode: 'banner' | 'full' | null = (() => {
    // Query parameter override takes precedence
    if (headerOverride === 'false') return null;
    if (headerOverride === 'true') return 'banner'; // Default to banner mode when forcing header on

    // Otherwise use survey config
    return surveyData.header_mode !== undefined
      ? surveyData.header_mode
      : (surveyData.show_header_banner ? 'full' : 'banner');
  })();

  // Check if we're on mobile device (not just small screen)
  useEffect(() => {
    const checkMobile = () => {
      // Check for touch device first (most reliable for mobile)
      const isTouchDevice = 'ontouchstart' in window || navigator.maxTouchPoints > 0;

      // Check for mobile user agent as fallback
      const isMobileUserAgent = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);

      // Check screen width (for desktop browsers resized small)
      const isSmallScreen = window.innerWidth <= 768;

      // Consider it mobile if it's a touch device OR mobile user agent
      // Don't rely only on screen width since tablets/phones in landscape can be wide
      setIsMobile(isTouchDevice || isMobileUserAgent || (isSmallScreen && !window.matchMedia('(hover: hover)').matches));
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Determine if we should use one page mode (mobile + onePageOnMobile flag)
  const useOnePageMode = isMobile && (surveyData.one_page_on_mobile ?? false);

  // Determine if the progress bar should be shown
  // Hide on mobile if onePageOnMobile is true
  const showProgressBar = useOnePageMode ? false : (surveyData.show_progress_bar ?? true);

  const genericSurveyLabel = useMemo(
    () => {
      return LanguageHelper.getSurveyLabel(languageCode);
    },
    [languageCode]
  );

  const surveyTitle = useMemo(() => {
    const langCode = languageCode.toLowerCase();
    const localeCandidates = langCode.includes('-')
      ? [langCode, langCode.split('-')[0]]
      : [langCode];

    if (surveyData.survey_title) {
      const entries = Object.entries(surveyData.survey_title).filter(
        ([, value]) => typeof value === 'string' && value.trim().length > 0
      );

      for (const locale of localeCandidates) {
        if (surveyData.survey_title[locale]) {
          return surveyData.survey_title[locale]!;
        }
      }

      for (const locale of localeCandidates) {
        const matchingEntry = entries.find(([key]) => key.split('-')[0] === locale);
        if (matchingEntry) {
          return matchingEntry[1];
        }
      }

      // Only fall back to English title if we're viewing in English or if there's no other option
      // For other languages, prefer the generic localized label
      if (langCode === 'en') {
        const enTitle = surveyData.survey_title.en?.trim();
        if (enTitle) {
          return enTitle;
        }
      }

      // If entries has content and we're not in English, just skip to generic label
      // Otherwise use first available as last resort
      if (entries.length > 0 && langCode !== 'en') {
        // Skip to generic label by not returning here
      } else {
        const firstAvailable = entries[0]?.[1];
        if (firstAvailable) {
          return firstAvailable;
        }
      }
    }

    return genericSurveyLabel;
  }, [languageCode, surveyData.survey_title, genericSurveyLabel]);

  const surveyDescription = useMemo(() => {
    const langCode = languageCode.toLowerCase();
    const localeCandidates = langCode.includes('-')
      ? [langCode, langCode.split('-')[0]]
      : [langCode];

    if (surveyData.survey_description) {
      const entries = Object.entries(surveyData.survey_description).filter(
        ([, value]) => typeof value === 'string' && value.trim().length > 0
      );

      for (const locale of localeCandidates) {
        if (surveyData.survey_description[locale]) {
          return surveyData.survey_description[locale]!;
        }
      }

      for (const locale of localeCandidates) {
        const matchingEntry = entries.find(([key]) => key.split('-')[0] === locale);
        if (matchingEntry) {
          return matchingEntry[1];
        }
      }

      // Fall back to first available description
      const firstAvailable = entries[0]?.[1];
      if (firstAvailable) {
        return firstAvailable;
      }
    }

    return ''; // No description
  }, [languageCode, surveyData.survey_description]);

  // Check if survey was already completed (localStorage)
  // Skip this check in preview mode
  useEffect(() => {
    if (isPreviewMode) return;
    if (!surveyData.one_response_per_person) return;
    const storageKey = `survey_completed_${surveyId}`;
    const completed = localStorage.getItem(storageKey);
    if (completed === 'true') {
      setAlreadyCompleted(true);
    }
  }, [surveyId, surveyData.one_response_per_person, isPreviewMode]);

  // Determine if the survey has ended
  const isSurveyClosed = useMemo(() => {
    if (!surveyData.survey_end) return false;

    const normalized = surveyData.survey_end.replace(' ', 'T');
    const endDate = new Date(normalized);

    if (Number.isNaN(endDate.getTime())) return false;

    return new Date() > endDate;
  }, [surveyData.survey_end]);

  // Collect all questions for validation purposes
  const allQuestions = surveyData.pages.flatMap(page => page.questions);

  // Get current page and its questions
  // In one-page mobile mode, show all questions from all pages
  const currentPage = surveyData.pages[currentPageIndex];
  const questions = useOnePageMode
    ? allQuestions
    : currentPage.questions;
  const showOneAtATime = useOnePageMode ? false : currentPage.show_questions_per_page;

  // Calculate question number offset for sequential display across pages
  const questionNumberOffset = useOnePageMode
    ? 0
    : surveyData.pages.slice(0, currentPageIndex).reduce((sum, page) => sum + page.questions.length, 0);

  // Initialize state management hooks
  const {
    currentQuestionIndex,
    responses,
    errors,
    goToNext,
    goToPrevious,
    updateResponse,
    setErrors,
    setQuestionIndex,
  } = useSurveyState(questions.length);

  const { validateQuestion, validateAllQuestions } = useSurveyValidation();

  // Ensure currentQuestionIndex is valid for the current questions array
  const safeQuestionIndex = Math.min(currentQuestionIndex, questions.length - 1);
  const currentQuestion = questions[safeQuestionIndex];

  // Initialize language from survey data on mount
  useEffect(() => {
    if (surveyData.languages && surveyData.languages.length > 0) {
      const defaultLanguage = surveyData.languages[0];
      // Only set language if it's supported by our LanguageHelper
      if (LanguageHelper.isLanguageSupported(defaultLanguage)) {
        setLanguage(defaultLanguage);
      }
    }
  }, [surveyData.languages, setLanguage]);

  // Reset question index when page changes
  // Store previous page index to detect direction of navigation
  const [previousPageIndex, setPreviousPageIndex] = useState(currentPageIndex);

  useEffect(() => {
    if (currentPageIndex !== previousPageIndex) {
      const newPageQuestions = surveyData.pages[currentPageIndex]?.questions;
      if (!newPageQuestions || newPageQuestions.length === 0) {
        // No questions on new page, stay on current page
        return;
      }

      // Check if we moved backwards (to previous page)
      if (currentPageIndex < previousPageIndex) {
        // Set to last question of the new (previous) page
        setQuestionIndex(Math.max(0, newPageQuestions.length - 1));
      } else {
        // Moving forward, reset to first question
        setQuestionIndex(0);
      }
      setPreviousPageIndex(currentPageIndex);
    }
  }, [currentPageIndex, previousPageIndex, setQuestionIndex, surveyData.pages]);

  // Re-translate existing error messages when language changes
  useEffect(() => {
    const errorKeys = Object.keys(errors);
    if (errorKeys.length === 0) {
      return;
    }

    const updatedErrors: ErrorsMap = {};
    let hasChanges = false;

    errorKeys.forEach((key) => {
      const questionNumber = Number(key);
      const question = allQuestions.find((q) => q.question_number === questionNumber);
      if (!question) {
        hasChanges = true;
        return;
      }

      const response = responses[questionNumber];
      const newError = validateQuestion(question, response);

      if (newError) {
        updatedErrors[questionNumber] = newError;
        if (errors[questionNumber] !== newError) {
          hasChanges = true;
        }
      } else {
        hasChanges = true;
      }
    });

    if (hasChanges) {
      setErrors(updatedErrors);
    }
  }, [languageCode, surveyTitle]);

  useEffect(() => {
    if (surveyTitle) {
      document.title = surveyTitle;
    } else {
      document.title = genericSurveyLabel;
    }
  }, [surveyTitle, genericSurveyLabel]);

  /**
   * Handles navigation to the next question or page
   * Validates the current question before allowing navigation
   */
  const handleNext = () => {
    if (showOneAtATime) {
      // Validate current question
      const response = responses[currentQuestion.question_number];
      const error = validateQuestion(currentQuestion, response);

      if (error) {
        setErrors({ ...errors, [currentQuestion.question_number]: error });
        return;
      }

      // Clear error if valid
      const newErrors = { ...errors };
      delete newErrors[currentQuestion.question_number];
      setErrors(newErrors);

      // Check if we're at the last question of the current page
      if (currentQuestionIndex === questions.length - 1) {
        // Move to next page
        if (currentPageIndex < surveyData.pages.length - 1) {
          setCurrentPageIndex(currentPageIndex + 1);
          // Question index will be reset by useEffect
        }
      } else {
        goToNext();
      }
    } else {
      // Validate all questions on current page
      const validationErrors = validateAllQuestions(questions, responses);
      setErrors(validationErrors);

      if (Object.keys(validationErrors).length === 0) {
        // Move to next page
        if (currentPageIndex < surveyData.pages.length - 1) {
          setCurrentPageIndex(currentPageIndex + 1);
        }
      } else {
        // Scroll to first error
        const firstErrorQuestion = questions.find((q) => validationErrors[q.question_number]);
        if (firstErrorQuestion) {
          const errorElement = document.querySelector(`[data-question-number="${firstErrorQuestion.question_number}"]`);
          if (errorElement) {
            errorElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
        }
      }
    }
  };

  /**
   * Handles navigation to the previous question or page
   */
  const handlePrevious = () => {
    if (showOneAtATime) {
      // Check if we're at the first question of the current page
      if (currentQuestionIndex === 0 && currentPageIndex > 0) {
        // Move to previous page (last question of that page)
        setCurrentPageIndex(currentPageIndex - 1);
        // Will be handled by useEffect when page changes
      } else if (currentQuestionIndex > 0) {
        goToPrevious();
      }
    } else {
      // Move to previous page
      if (currentPageIndex > 0) {
        setCurrentPageIndex(currentPageIndex - 1);
      }
    }
  };

  /**
   * Handles survey submission
   * Validates all questions across all pages and submits to API if valid
   */
  const handleSubmit = async () => {
    // Validate all questions across all pages
    const validationErrors = validateAllQuestions(allQuestions, responses);

    setErrors(validationErrors);

    if (Object.keys(validationErrors).length > 0) {
      console.error('Validation failed. Errors:', validationErrors);
      // Scroll to first error if showing all questions
      if (!showOneAtATime) {
        const firstErrorQuestion = questions.find((q) => validationErrors[q.question_number]);
        if (firstErrorQuestion) {
          const errorElement = document.querySelector(`[data-question-number="${firstErrorQuestion.question_number}"]`);
          if (errorElement) {
            errorElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
        }
      }
      return;
    }

    if (Object.keys(validationErrors).length === 0) {
      // Generate submission JSON with tracking data
      const submissionData = generateSubmissionJSON(
        surveyData,
        responses,
        languageCode,
        surveyId,
        marketoCookie,
        utmParameters
      );

      // Wrap submission in "submission" object and add e token
      const payload = {
        submission: submissionData,
        e: eToken || undefined
      };

      console.log('Survey Submission JSON:', payload);
      console.log('Survey Submission JSON (formatted):', JSON.stringify(payload, null, 2));

      // PREVIEW MODE: Mock submission without sending to API
      if (isPreviewMode) {
        console.log('🔍 PREVIEW MODE: Simulating successful submission (not sent to server)');
        setIsSubmitting(true);

        // Simulate network delay
        setTimeout(() => {
          setIsSubmitting(false);
          setIsSubmitted(true);
          console.log('✅ Preview mode submission complete');
        }, 1000);

        return;
      }

      console.log('Submitting to:', appConfig.submissionApiUrl);

      // Submit to API
      setIsSubmitting(true);
      setSubmissionError(null);

      try {
        const response = await fetch(appConfig.submissionApiUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(payload),
        });

        if (!response.ok) {
          // Try to parse error response for duplicate submission
          let errorMessage = LanguageHelper.getExtendedTranslation(languageCode, 'submissionFailed');
          let isDuplicateSubmission = false;
          
          // Check if response has content before trying to parse
          const contentType = response.headers.get('content-type');
          const hasJsonContent = contentType && contentType.includes('application/json');
          
          if (hasJsonContent) {
            try {
              const responseText = await response.text();
              if (responseText && responseText.trim().length > 0) {
                const errorData = JSON.parse(responseText);
                if (errorData.message) {
                  errorMessage = errorData.message;
                  // Check if this is a duplicate submission error
                  const errorMessageLower = errorData.message.toLowerCase();
                  isDuplicateSubmission = 
                    response.status === 409 || // Conflict status code
                    errorMessageLower.includes('duplicate') ||
                    errorMessageLower.includes('already submitted') ||
                    errorMessageLower.includes('already exists');
                }
              }
            } catch (parseError) {
              // If JSON parsing fails, check status code
              if (response.status === 409) {
                isDuplicateSubmission = true;
              }
              // Only log if it's not an empty response (which is expected for some errors)
              if (parseError instanceof SyntaxError && parseError.message.includes('JSON')) {
                // Empty response or invalid JSON - use status-based error handling
                if (response.status === 409) {
                  isDuplicateSubmission = true;
                } else if (response.status === 404) {
                  errorMessage = 'Submission endpoint not found. Please check your configuration.';
                }
              } else {
                logError('Could not parse error response:', parseError instanceof Error ? parseError : new Error(String(parseError)));
              }
            }
          } else {
            // No JSON content, check status code directly
            if (response.status === 409) {
              isDuplicateSubmission = true;
            } else if (response.status === 404) {
              errorMessage = 'Submission endpoint not found. Please check your configuration.';
            }
          }
          
          // If it's a duplicate submission, use the specific translation
          if (isDuplicateSubmission) {
            errorMessage = LanguageHelper.getExtendedTranslation(languageCode, 'duplicateSubmission');
            // Also save to localStorage if one_response_per_person is enabled (but not in preview mode)
            if (surveyData.one_response_per_person && !isPreviewMode) {
              const storageKey = `survey_completed_${surveyId}`;
              localStorage.setItem(storageKey, 'true');
            }
          }

          throw new Error(errorMessage);
        }

        // Success - show confirmation page
        console.log('Survey submitted successfully');

        // Save to localStorage if one_response_per_person is enabled (but not in preview mode)
        if (surveyData.one_response_per_person && !isPreviewMode) {
          const storageKey = `survey_completed_${surveyId}`;
          localStorage.setItem(storageKey, 'true');
        }

        setIsSubmitted(true);
      } catch (error) {
        logError('Error submitting survey:', error);
        const errorMessage = error instanceof Error ? error.message : LanguageHelper.getExtendedTranslation(languageCode, 'submissionFailed');
        setSubmissionError(errorMessage);
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  // Check if we're on the last page and last question (for showing submit button)
  const isLastPage = currentPageIndex === surveyData.pages.length - 1;
  const isLastQuestionOnPage = currentQuestionIndex === questions.length - 1;
  // In mobile one-page mode, always show Submit button
  const shouldShowSubmit = useOnePageMode || (isLastPage && (isLastQuestionOnPage || !showOneAtATime));

  // Check if we're on the first page and first question (for disabling previous button)
  const isFirstPage = currentPageIndex === 0;
  const isFirstQuestionOnPage = currentQuestionIndex === 0;
  // In mobile one-page mode, always hide Previous button (via shouldHidePrevious)
  const shouldDisablePrevious = useOnePageMode || (isFirstPage && (isFirstQuestionOnPage || !showOneAtATime));

  // In mobile one-page mode, disable Submit until all questions are answered
  // Otherwise, allow users to always attempt to go to the next step (validation happens on click)
  const shouldDisableNext = useOnePageMode 
    ? Object.keys(validateAllQuestions(allQuestions, responses)).length > 0 || isSubmitting
    : isSubmitting;

  /**
   * Compute overall progress steps across the whole survey.
   *
   * From the user's perspective:
   * - If a page shows one question at a time, each question on that page is a separate step.
   * - If a page shows all questions at once, that page is a single step.
   */
  const totalSteps = surveyData.pages.reduce((acc, page) => {
    return acc + (page.show_questions_per_page ? page.questions.length : 1);
  }, 0);

  const currentStepIndex = surveyData.pages.reduce((acc, page, index) => {
    if (index < currentPageIndex) {
      return acc + (page.show_questions_per_page ? page.questions.length : 1);
    }

    if (index === currentPageIndex) {
      return acc + (page.show_questions_per_page ? currentQuestionIndex : 0);
    }

    return acc;
  }, 0);

  // Localized survey closed message
  const surveyClosedMessages: Record<string, string> = {
    en: 'This survey is now closed and is no longer accepting responses.',
    fr: 'Ce sondage est maintenant fermé et n’accepte plus de réponses.',
  };

  const surveyClosedMessage =
    surveyClosedMessages[languageCode.toLowerCase()] ?? surveyClosedMessages.en;


  // If survey was already completed, show a message and do not render questions
  if (alreadyCompleted) {
    const messages = {
      en: {
        title: 'Thank You!',
        message: 'You have already completed this survey.',
      },
      fr: {
        title: 'Merci!',
        message: 'Vous avez déjà complété ce sondage.',
      },
    };

    const currentMessages = messages[languageCode.toLowerCase() as keyof typeof messages] || messages.en;

    return (
      <>
        <RadcliffeHeader
          languages={surveyData.languages}
          headerMode={headerMode}
        />
        <div className={`survey-container ${isCompactMode ? 'compact-mode' : ''}`}>
          {showTitle && (
            <SurveyHeader
              title={surveyTitle}
              description={undefined}
              availableLanguages={surveyData.languages}
            />
          )}
          <div className="survey-success-page">
            <div className="success-icon">
              <svg
                width="80"
                height="80"
                viewBox="0 0 80 80"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                aria-hidden="true"
              >
                <circle cx="40" cy="40" r="40" fill="#10B981" />
                <path
                  d="M25 40L35 50L55 30"
                  stroke="white"
                  strokeWidth="4"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </div>
            <h1 className="success-title">{currentMessages.title}</h1>
            <p className="success-message">{currentMessages.message}</p>
          </div>
        </div>
      </>
    );
  }

  // If survey is closed, show a simple message and do not render questions
  if (isSurveyClosed) {
    return (
      <>
        <RadcliffeHeader
          languages={surveyData.languages}
          headerMode={headerMode}
        />
        <div className={`survey-container ${isCompactMode ? 'compact-mode' : ''}`}>
          {showTitle && (
            <SurveyHeader
              title={surveyTitle}
              description={surveyDescription}
              availableLanguages={surveyData.languages}
            />
          )}
          <div className="survey-header">
            <p className="survey-description">{surveyClosedMessage}</p>
          </div>
        </div>
      </>
    );
  }

  // If survey is submitted, show success page
  if (isSubmitted) {
    return (
      <>
        <RadcliffeHeader
          languages={surveyData.languages}
          headerMode={headerMode}
        />
        <div className={`survey-container ${isCompactMode ? 'compact-mode' : ''}`}>
          {isPreviewMode && (
            <div style={{
              backgroundColor: 'rgba(255, 152, 0, 0.15)',
              color: '#000',
              padding: '12px 20px',
              textAlign: 'center',
              fontWeight: 'bold',
              fontSize: '14px',
              marginBottom: '20px',
              borderRadius: '4px',
              border: '2px solid rgba(245, 124, 0, 0.3)',
              boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
            }}>
              🔍 PREVIEW MODE - This submission was not saved
            </div>
          )}
          <SurveySuccessPage
            surveyTitle={surveyTitle}
            thankYouHtml={surveyData.thank_you_message}
          />
        </div>
      </>
    );
  }

  return (
    <>
      <RadcliffeHeader
        languages={surveyData.languages}
        headerMode={headerMode}
      />
      <div className={`survey-container ${isCompactMode ? 'compact-mode' : ''}`}>
        {isPreviewMode && (
          <div style={{
            backgroundColor: 'rgba(255, 152, 0, 0.15)',
            color: '#000',
            padding: '12px 20px',
            textAlign: 'center',
            fontWeight: 'bold',
            fontSize: '14px',
            marginBottom: '20px',
            borderRadius: '4px',
            border: '2px solid rgba(245, 124, 0, 0.3)',
            boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
          }}>
            🔍 PREVIEW MODE - Submissions will not be saved
          </div>
        )}

        {showTitle && (
          <SurveyHeader
            title={surveyTitle}
            description={surveyDescription}
            availableLanguages={surveyData.languages}
          />
        )}

        {showProgressBar && (
          <SurveyProgress
            currentIndex={currentStepIndex}
            totalQuestions={totalSteps}
          />
        )}

        <div className="questions-container">
          {showOneAtATime ? (
            // Show one question at a time
            <QuestionRenderer
              question={currentQuestion}
              value={responses[currentQuestion.question_number]}
              onChange={(value) => updateResponse(currentQuestion.question_number, value)}
              error={errors[currentQuestion.question_number]}
              displayNumber={questionNumberOffset + safeQuestionIndex + 1}
            />
          ) : (
            // Show all questions with sequential numbering
            questions.map((question, index) => (
              <QuestionRenderer
                key={question.question_number}
                question={question}
                value={responses[question.question_number]}
                onChange={(value) => updateResponse(question.question_number, value)}
                error={errors[question.question_number]}
                displayNumber={questionNumberOffset + index + 1}
              />
            ))
          )}
        </div>

        {submissionError && (
          <div className="submission-error">
            {submissionError}
          </div>
        )}

        {isSubmitting && (
          <div className="submission-overlay" role="status" aria-live="polite">
            <div className="submission-spinner" aria-hidden="true" />
            <span className="visually-hidden">
              {LanguageHelper.getExtendedTranslation(languageCode, 'submitting')}
            </span>
          </div>
        )}

        <SurveyNavigation
          showOneAtATime={showOneAtATime}
          currentIndex={currentQuestionIndex}
          totalQuestions={questions.length}
          onPrevious={handlePrevious}
          onNext={handleNext}
          onSubmit={handleSubmit}
          shouldShowSubmit={shouldShowSubmit}
          shouldDisablePrevious={shouldDisablePrevious}
          shouldDisableNext={shouldDisableNext}
          shouldHidePrevious={useOnePageMode}
        />
      </div>
    </>
  );
};
